export type AgreementPartnerKind =
    | 'goodLeap'
    | 'sunlight'
    | 'sunnova'
    | 'cash'
    | 'lightreach'
    | 'igs'
    | 'serviceFinance'
    | 'other';

export type AgreementToolVisibility = {
    /** Trinity: “Submit Application” column (GoodLeap + Sunlight only). */
    submitApplicationColumn: 'none' | 'goodLeap' | 'sunlight';
    sfdc: boolean;
    trinityContract: boolean;
    documents: boolean;
    applicationPortal: boolean;
    signing: boolean;
    credit: boolean;
    combinedPack: boolean;
};

const norm = (s: string) => s.replace(/\s/g, '').toLowerCase();

export function detectAgreementPartnerKind(row: { financePartner: string; financePartnerRaw: string }): AgreementPartnerKind {
    const x = norm(`${row.financePartnerRaw} ${row.financePartner}`);
    if (x.includes('good') && x.includes('leap')) return 'goodLeap';
    if (x.includes('sunlight')) return 'sunlight';
    if (x.includes('sunnova')) return 'sunnova';
    if (x.includes('lightreach')) return 'lightreach';
    if (x === 'igs' || x.includes('igs')) return 'igs';
    if (x.includes('service') && x.includes('finance')) return 'serviceFinance';
    if (x.includes('cash')) return 'cash';
    return 'other';
}

/** Which tool icons appear in the shared “Tools” column (Trinity varies by partner grid). */
export function agreementToolVisibility(kind: AgreementPartnerKind): AgreementToolVisibility {
    switch (kind) {
        case 'goodLeap':
            return {
                submitApplicationColumn: 'goodLeap',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: true,
                signing: true,
                credit: true,
                combinedPack: true,
            };
        case 'sunlight':
            return {
                submitApplicationColumn: 'sunlight',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: true,
                signing: true,
                credit: true,
                combinedPack: true,
            };
        case 'sunnova':
            /** Trinity Sunnova grid: sync + contract/doc/signing; no submit column; credit tool omitted. */
            return {
                submitApplicationColumn: 'none',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: true,
                signing: true,
                credit: false,
                combinedPack: true,
            };
        case 'igs':
        case 'lightreach':
            return {
                submitApplicationColumn: 'none',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: true,
                signing: true,
                credit: false,
                combinedPack: true,
            };
        case 'serviceFinance':
            return {
                submitApplicationColumn: 'none',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: true,
                signing: true,
                credit: false,
                combinedPack: true,
            };
        case 'cash':
            /** Cash grid: View Trinity proposal + SFDC + Trinity documents only (no partner submit / signing column set). */
            return {
                submitApplicationColumn: 'none',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: false,
                signing: false,
                credit: false,
                combinedPack: true,
            };
        default:
            return {
                submitApplicationColumn: 'none',
                sfdc: true,
                trinityContract: true,
                documents: true,
                applicationPortal: true,
                signing: true,
                credit: false,
                combinedPack: true,
            };
    }
}

/**
 * Min width for `Table` `scroll.x`. Must be ≥ sum of column widths + expand column (~55px), or columns
 * shrink and nowrap headers (e.g. SFDC Sync / Trinity Documents) overlap.
 * Align with `AGREEMENT_SFDC_SYNC_COL_WIDTH` / `AGREEMENT_TRINITY_DOCUMENTS_COL_WIDTH` in AgreementsModal.tsx.
 */
export function agreementGridScrollMinX(kind: AgreementPartnerKind): number {
    switch (kind) {
        case 'goodLeap':
            return 1080;
        case 'sunlight':
            return 1060;
        case 'sunnova':
        case 'igs':
        case 'lightreach':
            return 1180;
        case 'serviceFinance':
            return 1120;
        case 'cash':
        case 'other':
            return 1000;
        default:
            return 1080;
    }
}
